package com.devi3east.bashbuddy.dashboard.ui.components.dialogs

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.auth.AuthDialogStep
import com.devi3east.bashbuddy.auth.requests.SignInRequest
import com.devi3east.bashbuddy.auth.ui.AuthViewModel

@Composable
fun SignInAlertDialog(
    authViewModel: AuthViewModel,
    onDismiss: () -> Unit,
    onNavigateToSignUp: () -> Unit,
    onNavigateToCart: () -> Unit
) {
    val uiState by authViewModel.uiState.collectAsState()
    var step by remember { mutableStateOf<AuthDialogStep>(AuthDialogStep.SignIn) }

    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var passwordVisible by remember { mutableStateOf(false) }

    val canSubmit = email.isNotBlank() && password.length >= 8

    // Listen for successful auth
    LaunchedEffect(uiState.token) {
        if (uiState.token != null && uiState.profile != null) {
            step = AuthDialogStep.Success
        }
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(24.dp),
        tonalElevation = 6.dp,
        title = {
            Text(
                text = when (step) {
                    AuthDialogStep.SignIn -> "Sign In Required"
                    AuthDialogStep.Success -> "You're signed in"
                },
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center,
                style = MaterialTheme.typography.titleLarge.copy(
                    fontWeight = FontWeight.Bold
                )
            )
        },
        text = {
            when (step) {
                AuthDialogStep.SignIn -> {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {

                        Text(
                            text = "Sign in to continue to your cart.",
                            textAlign = TextAlign.Center,
                            style = MaterialTheme.typography.bodyMedium
                        )

                        OutlinedTextField(
                            value = email,
                            onValueChange = { input ->
                                email = input.replace(" ", "").lowercase()
                            },
                            enabled = !uiState.isLoading,
                            label = { Text("Email") },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        OutlinedTextField(
                            value = password,
                            onValueChange = { input ->
                                password = input.replace(" ", "")
                            },
                            enabled = !uiState.isLoading,
                            label = { Text("Password") },
                            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            trailingIcon = {
                                IconButton(onClick = { passwordVisible = !passwordVisible }) {
                                    Icon(
                                        imageVector = if (passwordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                                        contentDescription = null
                                    )
                                }
                            },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        if (!uiState.errorMessage.isNullOrBlank()) {
                            Text(
                                text = uiState.errorMessage!!,
                                color = MaterialTheme.colorScheme.error,
                                textAlign = TextAlign.Center,
                                style = MaterialTheme.typography.bodyMedium
                            )
                        }
                    }
                }

                AuthDialogStep.Success -> {
                    Text(
                        text = "Your account is ready. What would you like to do next?",
                        textAlign = TextAlign.Center,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        },

        confirmButton = {
            when (step) {
                AuthDialogStep.SignIn -> {
                    Button(
                        enabled = canSubmit && !uiState.isLoading,
                        shape = RoundedCornerShape(12.dp),
                        onClick = {
                            authViewModel.signIn(
                                SignInRequest(email.trim(), password)
                            )
                        }
                    ) {
                        if (uiState.isLoading) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(18.dp),
                                strokeWidth = 2.dp,
                                color = MaterialTheme.colorScheme.onPrimary
                            )
                        } else {
                            Text("Sign In", fontWeight = FontWeight.SemiBold)
                        }
                    }
                }

                AuthDialogStep.Success -> {
                    Button(
                        shape = RoundedCornerShape(12.dp),
                        onClick = onNavigateToCart
                    ) {
                        Text("Go to Cart", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        },

        dismissButton = {
            when (step) {
                AuthDialogStep.SignIn -> {
                    TextButton(onClick = onNavigateToSignUp) {
                        Text("Create Account")
                    }
                }

                AuthDialogStep.Success -> {
                    TextButton(onClick = onDismiss) {
                        Text("Continue browsing")
                    }
                }
            }
        }
    )
}